/**
 * Holds a credential only long enough to finish an email-verification flow.
 *
 * This deliberately uses module memory instead of Web Storage. Client-side
 * navigation keeps the module alive, while a refresh, closed tab, or expired
 * OTP flow drops the credential rather than leaving it readable at rest.
 */

const PENDING_AUTH_PASSWORD_TTL_MS = 15 * 60 * 1000;

type PendingAuthPassword = {
  email: string;
  password: string;
  expiresAt: number;
};

let pendingAuthPassword: PendingAuthPassword | null = null;

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function persistPendingAuthPassword(
  email: string,
  password: string,
  now = Date.now()
): void {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail || password.length === 0) {
    pendingAuthPassword = null;
    return;
  }

  pendingAuthPassword = {
    email: normalizedEmail,
    password,
    expiresAt: now + PENDING_AUTH_PASSWORD_TTL_MS,
  };
}

export function readPendingAuthPassword(
  email: string,
  now = Date.now()
): string | null {
  const normalizedEmail = normalizeEmail(email);
  const pending = pendingAuthPassword;

  if (!pending || pending.expiresAt <= now) {
    pendingAuthPassword = null;
    return null;
  }

  if (!normalizedEmail || pending.email !== normalizedEmail) {
    return null;
  }

  return pending.password;
}

export function clearPendingAuthPassword(): void {
  pendingAuthPassword = null;
}

export function resolvePendingAuthPassword(args: {
  inMemoryPassword: string;
  email: string;
  now?: number;
}): string {
  if (args.inMemoryPassword.length > 0) {
    return args.inMemoryPassword;
  }

  return readPendingAuthPassword(args.email, args.now) ?? '';
}
